import React, { useContext } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';

// COMPONENTS
import FocusableCardArt from 'src/universal/components/FocusableCardArt';
import ApplyOrUpgradeCta from './ApplyOrUpgradeCta';
import Tncs from './Tncs';
import {
  IconButton,
  Typography,
  MatchHeight
} from '@americanexpress/aqx-components';
import Tier1Badge from './Tier1&2Ribbon.js';

// SELECTORS
import { dispatchRemoveCardFromCompare } from 'src/universal/redux/compareCardsTray';
import {
  makeGetCardArt,
  makeGetCardName,
  makeGetAnnualFee,
  makeGetAsortedCardData
} from '../redux';
import { makeGetSelectedColorInfo } from 'src/universal/redux/cardColors.js';
import { FooterCtaVisibleContext } from 'page_modules/compare/index';

// UTILS
import { decideCardArtUrl } from 'src/universal/utils/cardColorChoices';
import {
  sendDTMClickEvent,
  mapUrlWithDtmLinkQuery
} from 'service_modules/models/dtm-tagging';

// STYLES
import styles from '../__styles__/cardSlot.scss?modules';

const CardSlot = ({
  card,
  cardArt,
  cardName,
  annualFee,
  colorInfo,
  removeCardFromCompare,
  smallCardArt,
  cardData
}) => {
  const cardArtUrl = decideCardArtUrl(cardArt, colorInfo, card);
  const cardArtLinkUrl = mapUrlWithDtmLinkQuery(cardData.linkUrl, cardData.dtm);
  const isFooterCtaVisible = useContext(FooterCtaVisibleContext);

  const closeIconClickEvent = () => {
    sendDTMClickEvent({
      eventAction: 'click',
      eventAsset: `CardArt_RemoveCard_${card}`
    });
  };

  const compareCardClickEvent = () => {
    sendDTMClickEvent({
      eventAction: 'click',
      eventAsset: `compare_card_art_${card}`
    });
  };
  return (
    <div className={styles.container}>
      <MatchHeight name="desktop-header-tier1-badge">
        <Tier1Badge
          shortName={card}
          styles={styles}
          defaultOfferText={cardData.defaultOfferButtonText}
          isHeader={true}
        />
      </MatchHeight>
      <div className={styles.imageButtonContainer}>
        <div
          className={clsx(styles.cardArtContainer, {
            [styles.smallCardArt]: smallCardArt
          })}
        >
          <FocusableCardArt
            onClick={compareCardClickEvent}
            imgUrl={cardArtUrl}
            imgAlt={cardData.imgAlt}
            ariaLabel={cardData.imgAlt}
            linkUrl={cardArtLinkUrl}
          />
        </div>
        <IconButton
          imageName="dls-icon-cancel-circle"
          onClick={() => {
            removeCardFromCompare(card);
            closeIconClickEvent();
          }}
          ariaLabel={`Remove ${cardName} from compare`}
        />
      </div>
      <div className={styles.cardNameContainer}>
        <MatchHeight name="cardName">
          <Typography className={styles.cardName}>{cardName}</Typography>
        </MatchHeight>
      </div>
      {!smallCardArt && (
        <React.Fragment>
          <div
            className={styles.annualFeeContainer}
            style={{ display: isFooterCtaVisible ? 'none' : 'block' }}
          >
            <MatchHeight name="annualFee">
              <Typography className={styles.annualFee}>{annualFee}</Typography>
            </MatchHeight>
          </div>
          <div style={{ display: isFooterCtaVisible ? 'none' : 'block' }}>
            <ApplyOrUpgradeCta shortName={card} position="Top" />
            <Tncs shortName={card} position="Top" />
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  removeCardFromCompare: shortName =>
    dispatchRemoveCardFromCompare(dispatch, { shortName })
});

const mapStateToProps = () => {
  const getCardArt = makeGetCardArt();
  const getCardName = makeGetCardName();
  const getAnnualFee = makeGetAnnualFee();
  const getSelectedColorInfo = makeGetSelectedColorInfo();
  const getAsortedCardData = makeGetAsortedCardData();

  return (state, { card }) => ({
    cardArt: getCardArt(state, card),
    cardName: getCardName(state, card),
    annualFee: getAnnualFee(state, card),
    colorInfo: getSelectedColorInfo(state, card),
    cardData: getAsortedCardData(state, card)
  });
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CardSlot);
